import { useCallback, useEffect, useRef, useState } from "react";
import { supabase, safeChannel } from "./supabase";
import { readCache, writeCache, cacheFresh } from "./useCachedQuery";

// 5-min TTL — short enough that boot data is roughly fresh, long
// enough to skip the heavy fetch on rapid tab switches / page
// reloads. Realtime fills the gap during the session. Per-user-
// per-table cache key so log-out/log-in or table switching doesn't
// cross-pollinate.
const SYNCED_LIST_TTL_MS = 5 * 60 * 1000;
// v2: v1 mirrored toDb(item) rows, which strip id/user_id — a warm
// relaunch hydrated id-less rows (UPDATE .eq("id", undefined) 22P02,
// broken realtime dedup). The mirror now stamps id/user_id, and the
// key bump discards every already-poisoned v1 cache in the wild.
const SYNCED_LIST_KEY = (table, userId) => `syncedList.v2:${table}:${userId}`;

/**
 * Generic "row list synced to a Supabase table" hook.
 *
 * Exposes the same API as `useState([])` — the caller can write:
 *   setList(prev => [...prev, newItem])
 * …and this hook will diff prev vs next and fire INSERT / UPDATE / DELETE
 * against Supabase in the background. That means components (Pantry, CookMode)
 * don't need to know about the database at all.
 *
 * Required shape of each item:
 *   - `id` — client-generated uuid (use `crypto.randomUUID()` on new items)
 *
 * Params:
 *   table       — Supabase table name
 *   userId      — the owning user (used to tag new inserts; reads rely on RLS)
 *   toDb        — (item) => row, for camelCase → snake_case conversion
 *   fromDb      — (row)  => item, the inverse
 *   refreshKey  — optional value; changing it triggers a reload (used to pick
 *                 up newly-shared family rows after a connection is accepted)
 *   selfOnly    — when true, filter loads to the current user's rows only.
 *   onRealtime  — optional callback fired for each realtime event from *other*
 *                 users. Shape: (eventType, row, oldRow) — row is already
 *                 mapped through fromDb. Use this to raise toasts.
 *
 * Returns [items, setItems, loading]
 */
export function useSyncedList({ table, userId, toDb, fromDb, refreshKey, selfOnly = false, onRealtime }) {
  // Hydrate from cache instantly so the boot has data without
  // waiting on the network. Cache stores DB-shaped rows; fromDb
  // runs at hydration time so callers see the mapped shape.
  const cachedRaw = userId ? readCache(SYNCED_LIST_KEY(table, userId), SYNCED_LIST_TTL_MS) : null;
  const [items, setItems] = useState(() => cachedRaw ? cachedRaw.map(fromDb) : []);
  const [loading, setLoading] = useState(() => userId ? cachedRaw === null : false);

  // Initial load (re-fires whenever refreshKey changes). TTL-gated
  // so a fresh-cache boot skips the network round-trip; realtime
  // below keeps in-session data current. refreshKey bumps force a
  // fresh fetch (e.g. post-mutation).
  useEffect(() => {
    let alive = true;
    if (!userId) {
      setItems([]);
      setLoading(false);
      return;
    }
    if (cacheFresh(SYNCED_LIST_KEY(table, userId), SYNCED_LIST_TTL_MS) && !refreshKey) {
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      let q = supabase.from(table).select("*");
      if (selfOnly) q = q.eq("user_id", userId);
      const { data, error } = await q;
      if (!alive) return;
      if (error) {
        console.error(`[${table}] load failed:`, error);
        setItems([]);
      } else {
        const raw = data || [];
        writeCache(SYNCED_LIST_KEY(table, userId), raw);
        setItems(raw.map(fromDb));
      }
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [table, userId, fromDb, refreshKey, selfOnly]);

  // Keep the latest onRealtime / fromDb in refs so we don't tear down the
  // subscription on every render. (onRealtime typically closes over
  // relationships.family which gets a new array reference on reload.)
  const onRealtimeRef = useRef(onRealtime);
  const fromDbRef = useRef(fromDb);
  useEffect(() => { onRealtimeRef.current = onRealtime; }, [onRealtime]);
  useEffect(() => { fromDbRef.current = fromDb; }, [fromDb]);

  // Realtime subscription — merges in changes from other users, and
  // reconciles our own changes against what the DB ultimately stored.
  //
  // BATCHING: Supabase streams realtime events one-at-a-time over
  // WebSocket, each on its own microtask, which React 18 does NOT
  // automatically batch (batching only covers event handlers +
  // effects, not async ticks). A commit that inserts 10 rows fires
  // 10 setItems calls and 10 top-level re-renders of whoever uses
  // this list (Kitchen, Shop Mode, etc.). On trips with bulk pantry
  // inserts + shopping list mark-purchased + trip_scans audit, that
  // compounds into visible UI jank.
  //
  // Fix: queue events in a ref, flush with a single setItems call
  // on the next microtask. Cost: one-tick visual lag for remote
  // changes (imperceptible). Win: ONE render per flush regardless
  // of event count.
  useEffect(() => {
    if (!userId) return;
    const pending = []; // queue of [eventType, newRow, oldRow]
    let flushScheduled = false;
    const flush = () => {
      flushScheduled = false;
      if (pending.length === 0) return;
      const batch = pending.splice(0, pending.length);
      setItems(prev => {
        let next = prev;
        for (const [eventType, newRow, oldRow] of batch) {
          if (eventType === "INSERT") {
            if (!next.some(i => i.id === newRow.id)) {
              // Allocate once per batch: grow the array only on the
              // first INSERT in this flush.
              if (next === prev) next = prev.slice();
              next.push(newRow);
            }
          } else if (eventType === "UPDATE") {
            const idx = next.findIndex(i => i.id === newRow.id);
            if (idx === -1) {
              if (next === prev) next = prev.slice();
              next.push(newRow);
            } else if (next[idx] !== newRow) {
              if (next === prev) next = prev.slice();
              next[idx] = newRow;
            }
          } else if (eventType === "DELETE") {
            const id = oldRow?.id;
            if (id != null) {
              const idx = next.findIndex(i => i.id === id);
              if (idx !== -1) {
                if (next === prev) next = prev.slice();
                next.splice(idx, 1);
              }
            }
          }
        }
        return next;
      });
    };
    const ch = safeChannel(`rt:${table}:${userId}`)
      .on("postgres_changes", { event: "*", schema: "public", table }, (payload) => {
        const mapRow = fromDbRef.current;
        const newRow = payload.new && Object.keys(payload.new).length ? mapRow(payload.new) : null;
        const oldRow = payload.old && Object.keys(payload.old).length ? payload.old : null;
        const fromOther =
          (payload.new && payload.new.user_id && payload.new.user_id !== userId) ||
          (payload.old && payload.old.user_id && payload.old.user_id !== userId);

        pending.push([payload.eventType, newRow, oldRow]);
        if (!flushScheduled) {
          flushScheduled = true;
          queueMicrotask(flush);
        }

        const cb = onRealtimeRef.current;
        if (fromOther && cb) cb(payload.eventType, newRow, oldRow);
      })
      .subscribe();
    return () => {
      // Flush any lingering events synchronously before tearing
      // down — avoids a memory-only write being lost on unmount.
      if (pending.length > 0) flush();
      supabase.removeChannel(ch);
    };
  }, [table, userId]);

  // Mirror items → localStorage so the next boot's cache hit
  // reflects the current state including realtime deltas + local
  // mutations. Cache stores raw DB-shaped objects; toDb is the
  // inverse of fromDb but on hydration we re-apply fromDb so the
  // outbound shape matches what the network fetch would return.
  // Persistence is best-effort — quota / private-mode just silently
  // skips and the next boot re-fetches.
  useEffect(() => {
    if (!userId) return;
    if (loading) return;
    try {
      const raw = items.map(it => {
        const row = typeof toDb === "function" ? toDb(it) : it;
        // toDb deliberately strips id/user_id (persistDiff stamps
        // them on insert) — but the mirror MUST keep them, or a
        // warm relaunch inside the 5-min TTL hydrates id-less rows:
        // edits then fire UPDATE .eq("id", undefined) → 22P02
        // banners, the differ collapses all rows onto one undefined
        // key, and realtime dedup never matches (duplicate rows).
        return { ...row, id: it.id, user_id: it.ownerId ?? userId };
      });
      writeCache(SYNCED_LIST_KEY(table, userId), raw);
    } catch {
      /* noop */
    }
  }, [items, userId, table, toDb, loading]);

  // StrictMode dedup: React invokes the updater below twice in dev
  // with the same `prev` reference to expose impurities. If
  // persistDiff runs both times it fires TWO Supabase inserts for the
  // same logical change; when the caller's updater is non-pure (e.g.
  // Kitchen's reducer used to call crypto.randomUUID() inside the
  // body) the two inserts get different ids, and the realtime
  // subscription re-adds the DB-only id back into local state,
  // yielding a "scanned once, appears twice" duplicate. We can't
  // force the caller's updater to be pure from here, but we can make
  // persistDiff fire exactly once per setList call by remembering
  // which `prev` we last persisted against.
  const lastPersistedPrevRef = useRef(null);
  // Debounce burst: gauge drags / typeahead / rapid edits used to fire
  // a Supabase write per tick, multiplying egress by ~30× on every
  // slider drag and broadcasting each one to every family member via
  // realtime. The pending ref captures the burst boundaries so a
  // 350ms quiet window collapses into ONE persistDiff against the
  // original prev (correct INSERT/UPDATE/DELETE intent regardless of
  // intermediate states).
  const pendingRef = useRef({ originalPrev: null, latestNext: null, timer: null });
  // FIFO write chain. persistDiff is async (it can sit in auth-gate
  // backoff for seconds after an iOS resume) — firing flushes
  // unserialized would let a LATER flush overtake a stalled earlier
  // one: the stalled INSERT of row X lands after B's UPDATE of X
  // (which matched 0 rows, silently), reverting the user's newest
  // edit with stale data. Chaining guarantees writes hit the DB in
  // setList order; while there's no session, later flushes queue
  // behind the gate instead of racing past it.
  const writeChainRef = useRef(Promise.resolve());
  const flushPending = useCallback(() => {
    const { originalPrev, latestNext, timer } = pendingRef.current;
    if (timer) clearTimeout(timer);
    pendingRef.current = { originalPrev: null, latestNext: null, timer: null };
    if (originalPrev !== null && latestNext !== null && userId) {
      writeChainRef.current = writeChainRef.current
        .then(() => persistDiff({ table, userId, toDb, prev: originalPrev, next: latestNext }))
        .catch(() => { /* persistDiff surfaces its own errors */ });
    }
  }, [table, userId, toDb]);

  // Flush any pending writes on unmount so the user doesn't lose edits
  // mid-burst on tab close / navigation away.
  useEffect(() => () => flushPending(), [flushPending]);

  // Diff-based setter. Accepts a value or functional updater, just like useState.
  const setList = useCallback(
    (updater) => {
      setItems(prev => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        const sameRef = lastPersistedPrevRef.current === prev;
        if (userId && !sameRef) {
          lastPersistedPrevRef.current = prev;
          // Capture the START of the burst (original prev) so the
          // eventual diff is against pre-burst state, not the latest
          // intermediate. Replace the latestNext on every tick.
          if (pendingRef.current.originalPrev === null) {
            pendingRef.current.originalPrev = prev;
          }
          pendingRef.current.latestNext = next;
          if (pendingRef.current.timer) clearTimeout(pendingRef.current.timer);
          // Inserts + deletes flush immediately. The 350ms debounce
          // exists to coalesce gauge-drag bursts (every slider tick
          // would otherwise be a separate UPDATE), but a fresh
          // INSERT or DELETE is always a user-explicit action — and
          // the debounce window meant a "scan + add + refresh"
          // sequence under 350ms lost the row entirely. Detect
          // structural diffs and flush now.
          const prevIds = new Set(prev.map(i => i.id));
          const nextIds = new Set(next.map(i => i.id));
          let structural = prevIds.size !== nextIds.size;
          if (!structural) {
            for (const id of nextIds) {
              if (!prevIds.has(id)) { structural = true; break; }
            }
          }
          if (structural) {
            // Fire on the next tick so React's commit lands first
            // — flushing inside the updater would persist against
            // pre-commit state.
            queueMicrotask(flushPending);
          } else {
            pendingRef.current.timer = setTimeout(flushPending, 350);
          }
        }
        return next;
      });
    },
    [userId, flushPending, table]
  );

  return [items, setList, loading];
}

// Fire-and-forget: diff prev vs next by id, then issue INSERTs / UPDATEs / DELETEs.
// Errors are logged; the local state is considered the source of truth.
async function persistDiff({ table, userId, toDb, prev, next }) {
  const prevById = new Map(prev.map(i => [i.id, i]));
  const nextById = new Map(next.map(i => [i.id, i]));

  const toInsert = [];
  const toUpdate = [];
  const toDelete = [];

  for (const [id, item] of nextById) {
    const old = prevById.get(id);
    if (!old) {
      toInsert.push({ ...toDb(item), id, user_id: userId });
    } else if (!shallowEqual(old, item)) {
      toUpdate.push({ id, row: toDb(item) });
    }
  }
  for (const [id] of prevById) {
    if (!nextById.has(id)) toDelete.push(id);
  }
  if (!toInsert.length && !toUpdate.length && !toDelete.length) return;

  // AUTH GATE — the "row violates row-level security policy" class.
  // When a resume-time token refresh fails (iOS froze the refresh
  // timer during suspension and the first post-wake network calls
  // flake), supabase-js silently falls back to sending the ANON key
  // on REST calls. auth.uid() evaluates NULL server-side and every
  // RLS-protected write dies with 42501, one banner per request.
  // These flushes fire on microtasks / just-thawed debounce timers,
  // beating App-level startAutoRefresh(). So: wait for a real
  // session before writing (getSession refreshes an expired token),
  // backing off for up to ~60s. That window is safe to sit in — the
  // caller serializes flushes through writeChainRef, so later edits
  // queue behind this one in order instead of racing past it. The
  // long ceiling matters because the give-up path genuinely LOSES
  // the diff: a dropped INSERT can't be recovered by a later edit
  // (it would diff as an UPDATE that matches 0 rows, silently), so
  // we only give up when the user is signed out / offline for a
  // full minute — and say so honestly.
  const AUTH_GATE_DEADLINE_MS = 60_000;
  const start = Date.now();
  let session = null;
  for (let attempt = 0; ; attempt++) {
    try {
      ({ data: { session } } = await supabase.auth.getSession());
    } catch { session = null; }
    if (session?.access_token) break;
    if (Date.now() - start > AUTH_GATE_DEADLINE_MS) break;
    await new Promise(r => setTimeout(r, Math.min(700 * (attempt + 1), 5000)));
  }
  if (!session?.access_token) {
    console.error(`[${table}] persist dropped — no auth session within ${AUTH_GATE_DEADLINE_MS}ms`, { inserts: toInsert.length, updates: toUpdate.length, deletes: toDelete.length });
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("mise:dbWriteFailed", {
        detail: {
          table, op: "auth",
          error: "couldn't reach your account for a minute (signed out or offline) — this change is on this device only and may not save",
        },
      }));
    }
    return;
  }

  if (toInsert.length) {
    supabase.from(table).insert(toInsert).then(({ error }) => {
      if (error) {
        console.error(`[${table}] insert failed:`, error, "rows:", toInsert);
        // Surface to the UI via a CustomEvent — the App listens and
        // shows a banner when DB writes fail silently. Without this
        // the user only sees "I added a thing but it's gone" with no
        // explanation.
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("mise:dbWriteFailed", {
            detail: { table, op: "insert", error: error.message || String(error), rows: toInsert.length },
          }));
        }
      }
    });
  }
  toUpdate.forEach(({ id, row }) => {
    supabase.from(table).update(row).eq("id", id).then(({ error }) => {
      if (error) {
        console.error(`[${table}] update ${id} failed:`, error, "row:", row);
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("mise:dbWriteFailed", {
            detail: { table, op: "update", error: error.message || String(error), id },
          }));
        }
      }
    });
  });
  if (toDelete.length) {
    supabase.from(table).delete().in("id", toDelete).then(({ error }) => {
      if (error) {
        console.error(`[${table}] delete failed:`, error, "ids:", toDelete);
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("mise:dbWriteFailed", {
            detail: { table, op: "delete", error: error.message || String(error), ids: toDelete },
          }));
        }
      }
    });
  }
}

function shallowEqual(a, b) {
  if (a === b) return true;
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  for (const k of ak) if (a[k] !== b[k]) return false;
  return true;
}
